import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_ENDPOINTS, buildUrl, SUPABASE_STORAGE_URL} from '../Utils/ApiConfig';
import ScoreShard from './Shards/ScoreShard';
import SkillsProgressBars from './Shards/SkillsProgressBars';
import EvaluationsOverview from './Shards/EvaluationsOverview';
import TabbedReportsSection from './Shards/TabbedReportsSection';
import { getColorForSeniority } from '../Utils/ColorUtils';
import CustomTooltip from '../Utils/CustomTooltip';

/**
 * TalentReport_Public Component
 * Public talent report page accessible without authentication
 * Route: /talent/:talent_id/:password_uuid
 */
const TalentReport_Public = () => {
  const { talent_id, password_uuid } = useParams();
  const [talent, setTalent] = useState(null);
  const [evals, setEvals] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState('evaluation');

  // Update reportType when talent data loads
  useEffect(() => {
    if (talent) {
      const hasEval = !!talent.talent_report_json;
      const hasAssessment = !!talent.talentProfileAssessmentJSON;
      
      if (hasEval) {
        setReportType('evaluation');
      } else if (hasAssessment) {
        setReportType('assessment');
      }
    }
  }, [talent]);

  useEffect(() => {
    const fetchPublicTalentReport = async () => {
      if (!talent_id) {
        setError('No talent ID provided');
        setLoading(false);
        return;
      }

      if (!password_uuid) {
        setError('No password provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Build URL with talent_id and password as query parameters
        const url = buildUrl(API_ENDPOINTS.GET_PUBLIC_TALENT_REPORT, {
          talent_id: talent_id,
          password: password_uuid
        });

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Try to get error message from response body
          try {
            const errorData = await response.json();
            if (errorData.error) {
              throw new Error(errorData.error);
            }
          } catch (jsonError) {
            // If we can't parse JSON, fall back to status text
          }
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        
        // Check if the response contains an error
        if (result.error) {
          throw new Error(result.error);
        }
        
        console.log('Public talent report data:', result[0]);
        const data = result[0];
        
        // Create talent object with required data from server response
        const talentObj = {
          fname: data?.fname || null,
          lname: data?.lname || null,
          roleCategory: data?.roleCategory || null,
          talent_report_json: data?.talent_report_json || null,
          eval_language_json: data?.eval_language_json || null,
          eval_softskills_json: data?.eval_softskills_json || null,
          eval_hardskills_json: data?.eval_hardskills_json || null,
          talentProfileJSON: data?.talentProfileJSON || null,
          talentProfileAssessmentJSON: data?.talentProfileAssessmentJSON || null  // ADD THIS!
        };
        setTalent(talentObj);
        
        // Extract and set evals from server response
        setEvals(data?.evals || null);
        
      } catch (error) {
        console.error('Error fetching public talent report:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicTalentReport();
  }, [talent_id, password_uuid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading talent report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 rounded-full p-6 mb-4 inline-block">
            <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Report</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Extract talent information using state variables
  const talentName = talent ? `${talent.fname || 'N/A'} ${talent.lname || 'N/A'}` : 'N/A';
  const roleCategory = talent?.roleCategory?.subCategory || 'N/A';
  const roleCategoryMain = talent?.roleCategory?.mainCategory || 'N/A';
  const totalYearsOfExperience = talent?.talentProfileJSON?.totalYearsOfExperience?.years || 'N/A';

  // Parse videos from evals
  const availableVideos = [];
  if (evals && Array.isArray(evals)) {
    evals.forEach((evaluation, evalIndex) => {
      if (evaluation.signedURL) {
        const evaluationName = evaluation?.evaluations?.name || `Evaluation ${evalIndex + 1}`;
        availableVideos.push({
          url: `${SUPABASE_STORAGE_URL}${evaluation.signedURL}`,
          evalIndex,
          title: evaluationName,
          evalId: evaluation.eval_id || `eval-${evalIndex + 1}`
        });
      }
    });
  }

  // Video navigation functions
  const goToNextVideo = () => {
    if (currentVideoIndex < availableVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  const goToPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Floating Bottom Toggle - Always show if we have any report data */}
        {(() => {
          const hasEvaluationData = !!talent?.talent_report_json;
          const hasProfileAssessment = !!talent?.talentProfileAssessmentJSON;
          
          return (
            <div 
              className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-1 bg-white rounded-full p-1 border-0"
              style={{
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
            >
              {/* All Evaluations Data Button with Tooltip */}
              <div className="relative group">
                <button
                  onClick={() => hasEvaluationData && setReportType('evaluation')}
                  disabled={!hasEvaluationData}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    reportType === 'evaluation'
                      ? 'bg-blue-500 text-white'
                      : hasEvaluationData
                      ? 'text-gray-600 hover:text-gray-800'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  All Evaluations Data
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                  <div className="bg-gray-900 text-white text-sm rounded-lg py-3 px-4 w-80 shadow-lg">
                    <p className="text-center">
                      Report based on data from all completed evaluations by this talent.
                    </p>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-8 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Assessment Data Button with Tooltip */}
              <div className="relative group">
                <button
                  onClick={() => hasProfileAssessment && setReportType('assessment')}
                  disabled={!hasProfileAssessment}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    reportType === 'assessment'
                      ? 'bg-blue-500 text-white'
                      : hasProfileAssessment
                      ? 'text-gray-600 hover:text-gray-800'
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Profile Assessment Data
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                  <div className="bg-gray-900 text-white text-sm rounded-lg py-3 px-4 w-80 shadow-lg">
                    <p className="text-center">
                      Report based on data from the talent's profile assessment (ie. resume analysis).
                    </p>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-8 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Combined Talent Info and Video Section - No top header bar with job */}
        <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-10">
            {/* Left Column - Talent Information */}
            <div className="w-full md:w-2/5">
              <div className="space-y-4">
                {/* Talent Info */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{talentName}</h2>
                  <p className="text-lg text-gray-700 mb-3">{roleCategory}</p>
                  
                  {/* Additional Info */}
                  <div className="space-y-2">
                    {roleCategoryMain && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Role Category:</span> {roleCategoryMain}
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Years of Experience:</span> {totalYearsOfExperience}
                    </div>
                    
                    {(() => {
                      const reportData = reportType === 'evaluation'
                        ? talent?.talent_report_json
                        : talent?.talentProfileAssessmentJSON;
                      
                      return (
                        <>
                          {reportData?.seniority && (
                            <div className="flex items-center text-sm">
                              <span className="font-medium text-gray-600 mr-2">Inferred Seniority:</span>
                              <span 
                                className="px-2 py-1 rounded-full text-white font-medium text-xs"
                                style={{ backgroundColor: getColorForSeniority(reportData.seniority.level) }}
                              >
                                {reportData.seniority.level || "N/A"}
                              </span>
                            </div>
                          )}
                          
                          {(reportData?.conclusion?.value || reportData?.summary?.value) && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                              <p className="text-base text-gray-800 leading-relaxed">
                                {reportData?.conclusion?.value || reportData?.summary?.value}
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Videos */}
            <div className="w-full md:w-3/5">
              {availableVideos.length > 0 ? (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    {availableVideos.length > 1 && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={goToPreviousVideo}
                          disabled={currentVideoIndex === 0}
                          className={`p-1 rounded ${
                            currentVideoIndex === 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="text-sm text-gray-600">
                          {currentVideoIndex + 1} of {availableVideos.length}
                        </span>
                        <button
                          onClick={goToNextVideo}
                          disabled={currentVideoIndex === availableVideos.length - 1}
                          className={`p-1 rounded ${
                            currentVideoIndex === availableVideos.length - 1
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      key={availableVideos[currentVideoIndex]?.url}
                      controls
                      preload="metadata"
                      muted
                      playsInline
                      className="w-full h-full"
                      src={availableVideos[currentVideoIndex]?.url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {availableVideos[currentVideoIndex]?.title}
                  </p>
                </div>
              ) : (
                <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-2xl font-semibold text-gray-500">No Video Available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metrics Row */}
        {(() => {
          const reportData = reportType === 'evaluation'
            ? talent?.talent_report_json
            : talent?.talentProfileAssessmentJSON;
          
          const hasGeneralAbility = reportData?.generalAbility?.value || reportData?.generalAbility?.grade;
          
          return hasGeneralAbility && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* General Ability Score */}
              <ScoreShard
                title="General Ability"
                primaryValue={reportData?.generalAbility?.value || reportData?.generalAbility?.grade || 0}
                primaryLabel="General Ability"
                secondaryValue={null}
                secondaryLabel=""
                rationale={reportData?.generalAbility?.rationale || "N/A"}
              />

              {/* Resume Accuracy - Only shown for evaluation mode */}
              {reportType === 'evaluation' && (
                <ScoreShard
                  title="Resume Accuracy"
                  primaryValue={reportData?.resumeAccuracy?.value || 0}
                  primaryLabel="Resume Accuracy"
                  secondaryValue={null}
                  secondaryLabel=""
                  rationale={reportData?.resumeAccuracy?.rationale || "N/A"}
                />
              )}
            </div>
          );
        })()}

        {/* Skills Progress Bars Section */}
        {(() => {
          const skills = reportType === 'evaluation'
            ? (talent?.eval_hardskills_json?.hardSkills?.items || [])
            : (talent?.talentProfileAssessmentJSON?.hardSkills?.items || []);
          
          return skills.length > 0 && (
            <div className="mb-6">
              <SkillsProgressBars
                title="Top Skills (Quick View)"
                skills={skills}
                showSections={true}
              />
            </div>
          );
        })()}

        {/* Evaluations Overview Section */}
        <div className="mb-6">
          <EvaluationsOverview evals={(evals || []).filter(evaluation => evaluation.status === 'es_completed')} hideCheatingProbability={true} />
        </div>

        {/* Tabbed Reports Section */}
        <TabbedReportsSection 
          candidate={null} 
          talent={(() => {
            if (reportType === 'evaluation') {
              return {
                talent_report_json: talent?.talent_report_json || null,
                eval_language_json: talent?.eval_language_json || null,
                eval_softskills_json: talent?.eval_softskills_json || null,
                eval_hardskills_json: talent?.eval_hardskills_json || null,
                talentProfileAssessmentJSON: talent?.talentProfileAssessmentJSON || null
              };
            } else {
              // For profile assessment, map the data to match the expected structure
              const normalizedSoftSkills = talent?.talentProfileAssessmentJSON?.softSkills?.items?.map(skill => ({
                ...skill,
                score: skill.grade || skill.score
              })) || [];

              return {
                talent_report_json: talent?.talentProfileAssessmentJSON || null,
                eval_language_json: null,
                eval_softskills_json: normalizedSoftSkills.length > 0 ? normalizedSoftSkills : null,
                eval_hardskills_json: talent?.talentProfileAssessmentJSON ? {
                  hardSkills: talent.talentProfileAssessmentJSON.hardSkills,
                  expertise: talent.talentProfileAssessmentJSON.expertise
                } : null,
                talentProfileAssessmentJSON: talent?.talentProfileAssessmentJSON || null
              };
            }
          })()} 
          evals={evals} 
          hideCheatingProbability={true}
          reportType={reportType}
        />

      </div>
    </div>
  );
};

export default TalentReport_Public;
